import styled from '@emotion/styled';
import React, { PropsWithChildren } from 'react';
import { Typography } from '../../atoms/typography/typography';
import { Icon } from '../../atoms/icon/icon';
import { css } from '@emotion/react';
import { NavbarBottomSheet, Ripple, Badge } from '@taleemabad/dsm/components';
import { BreakpointSizes, mq } from '@taleemabad/dsm/themes';
import { MenuCardProps } from '../menu-card/menu-card';
import { Can } from '@taleemabad/shared/utils';
import { useAccessControlAbility } from '@taleemabad/shared';

export interface NavBarItem {
  label: string;
  icon: string;
  id: number;
  path?: string;
  analyticsKey: string;
  visible?: boolean;
  isNew?: boolean;
}

export interface NavBarProps {
  items: NavBarItem[];
  bottomSheetItems: MenuCardProps[];
  activeItemKey: number;
  fixed?: boolean;
  onChange: (item: NavBarItem) => void;
  onBottomSheetItemClick?: (id: number) => void;
}

export const NavBar = ({
  items,
  bottomSheetItems,
  activeItemKey,
  fixed,
  onChange,
  onBottomSheetItemClick,
}: NavBarProps) => {
  return (
    <StyledNavBarContainer css={fixed && fixedStyled}>
      {items.map((item) => {
        const isActive = activeItemKey === item.id;
        return (
          <NavBarItemContainer
            key={item.id}
            className={`rk-nav-bar-item-container${isActive ? ' active' : ''}`}
            onClick={() => onChange(item)}
          >
            <Ripple />
            <StyledActiveIndicator isActive={isActive}>
              <NavBarItemIcon isActive={isActive} name={item.icon} />
            </StyledActiveIndicator>
            <NavBarItemLabel isActive={isActive}>{item.label}</NavBarItemLabel>
            {item.isNew && (
              <AbsolutePositioned>
                <Badge
                  type="filled"
                  size="small"
                  label="New"
                  color="var(--md-sys-color-schemes-error)"
                />
              </AbsolutePositioned>
            )}
          </NavBarItemContainer>
        );
      })}
      <NavBarItemContainer
        key="bottomSheet"
        className="rk-nav-bar-item-container"
        onClick={() => {}}
      >
        <NavbarBottomSheet
          menuCards={bottomSheetItems}
          onCardClick={onBottomSheetItemClick}
        />
      </NavBarItemContainer>
    </StyledNavBarContainer>
  );
};

const fixedStyled = css`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  z-index: 1000;
`;

const StyledNavBarContainer = styled.div`
  display: none;
  flex-direction: row;
  width: 100%;
  ${mq(BreakpointSizes.xs)} {
    display: flex;
    position: fixed;
  }
`;

interface NavBarItemContainerProps extends PropsWithChildren {
  className?: string;
  onClick: () => void;
}

const NavBarItemContainer = ({
  children,
  className = '',
  onClick,
}: NavBarItemContainerProps) => {
  return (
    <StyledNavBarItemContainer className={className} onClick={onClick}>
      {children}
    </StyledNavBarItemContainer>
  );
};

const StyledNavBarItemContainer = styled.div`
  background-color: var(--md-sys-color-surface-container);
  height: 80px;
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  justify-content: center;
  cursor: pointer;
  position: relative;
`;

interface NavBarItemLabelProps extends PropsWithChildren {
  isActive?: boolean;
}

const NavBarItemLabel = ({ children }: NavBarItemLabelProps) => {
  return <Typography variant="textLabelSmallNeutral10">{children}</Typography>;
};

interface StyledActiveIndicatorProps {
  isActive?: boolean;
}

const StyledActiveIndicator = styled.div<StyledActiveIndicatorProps>`
  height: 32px;
  width: 64px;
  border-radius: 16px;
  background-color: ${(props) =>
    !props.isActive ? 'transparent' : 'var(--md-ref-primary-50)'};
  margin-bottom: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const AbsolutePositioned = styled.div`
  position: absolute;
  top: var(--rk-spacing-lg);
  right: var(--rk-spacing-lg);
`;

interface NavBarItemIconProps {
  name: string;
  isActive?: boolean;
}

const NavBarItemIcon = ({ name, isActive = false }: NavBarItemIconProps) => {
  return (
    <StyledNavBarItemIcon
      name={name}
      emotionCss={css`
        color: ${isActive
          ? 'var(--md-ref-neutral-99)'
          : 'var(--md-sys-color-on-surface-variant)'};
      `}
    />
  );
};

const StyledNavBarItemIcon = styled(Icon)``;
